<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :items="items" class="mc-page-three" />
      <p class="mb-4">
        {{ $t('maintenance.conflict.desc') }}
      </p>

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="d-flex justify-content-end">
          <McButton
            @click="$router.go(-1)"
            class="me-2"
            variant="secondary"
            icon="icon-outline_close"
            :text="$t('global.cancel')"
          />
          <McButton
            variant="primary"
            type="button"
            @click="handleSubmit"
            :text="$t('global.action.continue')"
            icon="icon-outline_save"
          />
        </div>
      </div>
    </div>
    <b-card>
      <div class="d-flex flex-column align-items-center" v-if="data.length === 0">
        <figure>
          <img src="@/assets/images/success-icon.png" alt />
        </figure>
        <h5>{{ $t('maintenance.conflict.noConflict') }}</h5>
        <span class="mb-3">{{ $t('maintenance.conflict.text.backToHome') }}</span>
        <McButton
          variant="primary"
          type="button"
          @click="handleSubmit"
          :text="$t('global.action.continue')"
          icon="icon-outline_arrow-right"
        />
      </div>
      <div class="accordion mc-accordion" role="tablist" v-if="data.length > 0">
        <div class="mc-accordion-item" v-for="(row, index) in data" :key="index">
          <div header-tag="header" role="tab" class="mc-accordion__header">
            <div class="d-flex justify-content-between mc-list-info__top">
              <div>
                <span>{{ $t('global.object.licensePlate') }}</span>
                <h5>
                  {{
                    row.vehicle.licensePlate +
                    '/ ' +
                    row.vehicle.vehicleType.type +
                    '/ ' +
                    row.vehicle.vehicleType.category
                  }}
                </h5>
              </div>
              <div>
                <span>{{ $t('global.object.fleetGroup') }}</span>
                <h5>
                  {{
                    row.vehicle.vehicle2Vgroup?.vgroup?.name
                      ? row.vehicle.vehicle2Vgroup?.vgroup?.name
                      : '-'
                  }}
                </h5>
              </div>
              <div>
                <span>{{ $t('maintenance.field.odoFleetNow') }}</span>
                <h5>
                  {{
                    row.vehicle.odometer == null
                      ? '-'
                      : `${thousandFormat(row.vehicle.odometer)} Km`
                  }}
                </h5>
              </div>
              <div>
                <span>{{ $t('maintenance.form.totalHourmeter') }}</span>
                <h5>
                  {{ row.vehicle.hourmeter == null ? '-' : minuteToHour(row.vehicle.hourmeter) }}
                </h5>
              </div>
              <div>
                <span>{{ $t('maintenance.conflict.amount') }}</span>
                <h5>{{ row.serviceSchedules.length }} {{ $t('global.scheduled') }}</h5>
              </div>
              <div>
                <i class="mc-fa icon-outline_trash" @click="deleteConflict(index)"></i>
                <span v-b-toggle="'accordion-' + index">
                  <i class="mc-fa icon-fill_arrow-ios-up"></i>
                  <i class="mc-fa icon-fill_arrow-ios-down"></i>
                </span>
              </div>
            </div>
          </div>
          <b-collapse :id="'accordion-' + index" role="tabpanel">
            <div class="mc-accordion__body">
              <b-alert show variant="warning" class="mc-alert d-flex justify-content-start mt-3">
                <i class="mc-fa icon-fill_alert-triangle"></i>
                <span class="mc-alert__text"
                  >{{ $t('maintenance.conflict.text.conflict') }}
                  <strong>{{ row.serviceSchedules.length }}</strong>
                  {{ $t('maintenance.conflict.text.scheduler') }}.</span
                >
              </b-alert>
              <div class="table-responsive">
                <b-table
                  :items="row.serviceSchedules"
                  :fields="fields"
                  :busy="isBusy"
                  responsive="sm"
                >
                  <template #table-busy>
                    <div class="text-center text-danger my-2">
                      <b-spinner class="align-middle" />
                      <strong>{{ $t('global.text.loading') }}...</strong>
                    </div>
                  </template>

                  <template #cell(service2Ssgroup)="data">
                    {{ $t('maintenance.form.every') }}
                    <span class="text-danger" v-if="data.item.type === 'Hourmeter'">
                      {{ minuteToHour(data.value.intervalHourmeter) }}
                    </span>
                    <span v-if="data.item.type === 'DistanceAndTime'">
                      <span class="text-danger"> {{ `${data.value.intervalDistance} Km` }} </span>
                      &
                      <span class="text-danger"
                        >{{ data.value.intervalTime }}
                        {{ convertPeriodName(data.value.intervalPeriod) }}</span
                      >
                    </span>
                  </template>
                  <template #cell(method)="data">
                    {{ `${data.item.oldMethod} -> ${data.item.newMethod}` }}
                  </template>
                  <template #cell(newInterval)="data">
                    {{ $t('maintenance.form.every') }}
                    <span class="text-success" v-if="data.value.type === 'Hourmeter'">
                      {{ minuteToHour(data.value.intervalHourmeter) }}
                    </span>
                    <span v-if="data.value.type === 'DistanceAndTime'">
                      <span class="text-success">
                        {{ `${data.value.intervalDistance} Km` }}
                      </span>
                      &
                      <span class="text-success">
                        {{ data.value.intervalTime }}
                        {{ convertPeriodName(data.value.intervalPeriod) }}
                      </span>
                    </span>
                  </template>
                  <template #cell(nextService)="data">
                    <span v-if="data.item.newInterval.type === 'DistanceAndTime'">
                      {{
                        `${data.item.nextServiceDistance} Km & ${momentDateShortMonth(
                          data.item.nextServiceTime
                        )}`
                      }}
                    </span>
                    <span v-else>
                      {{ minuteToHour(data.item.nextServiceHourmeter) }}
                    </span>
                  </template>
                </b-table>
              </div>
            </div>
          </b-collapse>
        </div>
      </div>
    </b-card>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import McButton from '@/components/McButton'
import Layout from '@/router/layouts/main'
import { Helper } from '@/mixin/helper'
import {
  GET_SERVICE_SCHEDULES_BY_VEHICLE,
  GET_SERVICE_SCHEDULE_GROUP_BY_ID,
} from '@/services/api/maintenance/serviceSchedules'
import { CREATE_VEHICLES_2_SSGROUP } from '@/services/api/maintenance/ssgroup'
import { jwtDecode } from '@/helpers/jwt.js'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Servis',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
  },

  mixins: [Helper],

  data() {
    return {
      data: [],
      filter: {
        vehicleId: [],
        serviceId: [],
      },
      deletedConflict: [],
      isBusy: false,
      selectionMode: 'Selection',
    }
  },

  computed: {
    title() {
      return this.$t('maintenance.conflict.title')
    },

    items() {
      return [
        {
          text: this.$t('maintenance.title'),
          to: { name: 'Service' },
        },
        {
          text: this.$t('maintenance.frontPage.title.serviceGroup'),
          to: { name: 'Service Group' },
        },
        {
          text: this.$t('maintenance.conflict.conflictScheduler'),
        },
      ]
    },

    fields() {
      return [
        {
          key: 'service',
          label: this.$t('maintenance.field.schedulerComponent'),
          sortable: false,
          formatter: (val) => {
            return `${val.name} (${val.serviceAction.action})`
          },
        },
        {
          key: 'oldScheduled',
          label: this.$t('maintenance.field.previousGroup'),
          sortable: false,
        },
        {
          key: 'method',
          label: this.$t('maintenance.field.method'),
          sortable: false,
        },
        {
          key: 'service2Ssgroup',
          label: this.$t('maintenance.field.oldInterval'),
          sortable: false,
          formatter: (val, key, item) => {
            if (val) {
              const find = val.find((s) => s.serviceId == item.serviceId)
              return {
                intervalDistance: find.intervalDistance,
                intervalTime: find.intervalTime,
                intervalPeriod: find.intervalPeriod,
                intervalHourmeter: find.intervalHourmeter,
              }
            }

            return {
              intervalDistance: item.intervalDistance,
              intervalTime: item.intervalTime,
              intervalPeriod: item.intervalPeriod,
              intervalHourmeter: item.intervalHourmeter,
            }
          },
        },
        {
          key: 'newInterval',
          label: this.$t('maintenance.field.newInterval'),
          sortable: false,
        },
        {
          key: 'nextService',
          label: this.$t('maintenance.field.nextService'),
          sortable: false,
        },
      ]
    },
  },

  async created() {
    const encryptedIds = this.$route.query.ids
    const decodedData = await jwtDecode(encryptedIds)

    this.filter.vehicleId = decodedData.payload.vehicleId
    this.filter.serviceId = decodedData.payload.serviceId
    this.selectionMode = decodedData.payload.selectionMode

    const { data: res } = await this.$apollo.query({
      query: GET_SERVICE_SCHEDULE_GROUP_BY_ID,
      fetchPolicy: 'no-cache',
      variables: {
        filter: {
          ssgroupId: Number(this.$route.params.ssgroupId),
        },
      },
    })

    const groupServices = res.ssgroup.ssgroup.service2Ssgroup

    const { data } = await this.$apollo.query({
      client: 'clientLowPrio',
      query: GET_SERVICE_SCHEDULES_BY_VEHICLE,
      fetchPolicy: 'no-cache',
      variables: {
        filter: {
          pagination: {
            show: 999,
            page: 1,
          },
          serviceId: this.filter.serviceId,
          vehicle: {
            selectionMode: this.selectionMode,
            vehicleId: this.filter.vehicleId,
          },
        },
      },
    })

    const calculateStartServiceAt = (interval, initialValue, method, period) => {
      const isFloating = method === 'Floating'

      if (typeof initialValue === 'number') {
        if (isFloating) return Number(parseFloat(initialValue).toFixed(2)) + Number(interval)

        const division = Number(parseFloat(initialValue).toFixed(2)) / Number(interval)

        return (division % 1 === 0 ? division + 1 : Math.ceil(division)) * Number(interval)
      }

      const start = isFloating ? new Date(initialValue) : new Date(initialValue.getFullYear(), 0, 1)

      if (period === 'm') {
        let multiplier = initialValue.getMonth() / interval
        if (multiplier % 1 === 0) multiplier += 1
        else multiplier = Math.ceil(multiplier)

        return new Date(start.setMonth(start.getMonth() + interval * (isFloating ? 1 : multiplier)))
      }

      if (period === 'y') return new Date(start.setFullYear(start.getFullYear() + interval))

      const addOn = interval * (period === 'd' ? 86400000 : 604800000)

      const diffDate = Math.abs(start.valueOf() - initialValue.valueOf())
      const division = diffDate / addOn

      return new Date(start.valueOf() + addOn * (division % 1 === 0 ? 1 : Math.ceil(division)))
    }
    if (data.serviceSchedulesByVehicle.isSuccessful) {
      this.data = data.serviceSchedulesByVehicle.serviceSchedulesByVehicle.data
      this.data.forEach((el) => {
        this.isBusy = true
        el.serviceSchedules.forEach(async (s) => {
          const newService = groupServices.find((x) => x.serviceId == s.serviceId)
          s.oldMethod = s.method
          s.newMethod = newService.method
          s.newInterval = {
            intervalDistance: newService.intervalDistance,
            intervalTime: newService.intervalTime,
            intervalPeriod: newService.intervalPeriod,
            intervalHourmeter: newService.intervalHourmeter,
            type: newService.type,
          }
          s.nextServiceDistance =
            newService.type == 'DistanceAndTime'
              ? calculateStartServiceAt(
                  newService.intervalDistance,
                  this.data[0].vehicle.odometer,
                  newService.method,
                  null
                )
              : null
          s.nextServiceTime =
            newService.type == 'DistanceAndTime'
              ? calculateStartServiceAt(
                  newService.intervalTime,
                  new Date(),
                  newService.method,
                  newService.intervalPeriod
                )
              : null
          s.nextServiceHourmeter =
            newService.type == 'Hourmeter'
              ? calculateStartServiceAt(
                  newService.intervalHourmeter,
                  this.data[0].vehicle.hourmeter == null ? 0 : this.data[0].vehicle.hourmeter,
                  newService.method,
                  null
                )
              : null
          s.oldScheduled = s.ssgroup?.name ? s.ssgroup?.name : '-'
          this.isBusy = false
        })
      })
    }
  },
  methods: {
    deleteConflict(index) {
      const vehicleId = this.data[index].vehicle.id
      const indexToRemove = this.filter.vehicleId.indexOf(vehicleId)
      this.filter.vehicleId.splice(indexToRemove, 1)

      this.data.splice(index, 1)
    },
    async handleSubmit() {
      const res = await this.$apollo.mutate({
        mutation: CREATE_VEHICLES_2_SSGROUP,
        variables: {
          input: {
            ssgroupId: Number(this.$route.params.ssgroupId),
            vehicle: {
              selectionMode: this.selectionMode,
              vehicleId: this.filter.vehicleId,
            },
          },
        },
      })

      if (res.data.createVehicle2Ssgroup.isSuccessful) {
        this.$router.push({
          name: 'Detail Group Service',
          params: { ssgroupId: this.$route.params.ssgroupId },
        })
      }
    },
  },
}
</script>
